import debounce from "lodash/debounce";
import { makeAutoObservable, reaction } from "mobx";
import * as monaco from "monaco-editor";
import { Monaco } from "@monaco-editor/react";

import {
  makeHighlightDec,
  makeHighlightLines,
  validateSemgrepYaml,
} from "@pages/editor/utils";

import { Workbench } from "./Workbench";

export class Editors {
  workbench: Workbench;
  ruleEditor?: monaco.editor.IStandaloneCodeEditor;
  ruleEditorValue?: string;
  ruleMonaco?: Monaco;
  targetEditor?: monaco.editor.IStandaloneCodeEditor;
  targetEditorValue?: string;
  targetMonaco?: Monaco;
  latestHighlightIds: string[] = [];

  constructor(workbench: Workbench) {
    this.workbench = workbench;
    makeAutoObservable(this, { workbench: false }, { autoBind: true });

    // whenever the ruleEditor or targetEditor change (like when a new rule is selected from
    // the sidebar) register the keyboard shortcuts
    reaction(
      () => [this.ruleEditor, this.targetEditor, this.workbench.bundle],
      this.registerKeyboardShortcuts
    );

    // whenever the result changes, render the new highlights
    reaction(
      () => [this.workbench.result, this.workbench.result?.highlights],
      this.renderTargetHighlights
    );

    // whenver the ruleEditorValue changes then debounce the markers
    reaction(() => this.ruleEditorValue, debounce(this.updateRuleMarkers, 500));
  }

  // onMount

  onRuleEditorMount(
    editorInstance: monaco.editor.IStandaloneCodeEditor,
    monacoInstance: Monaco
  ) {
    this.ruleEditor = editorInstance;
    this.ruleMonaco = monacoInstance;
  }

  onTargetEditorMount(
    editorInstance: monaco.editor.IStandaloneCodeEditor,
    monacoInstance: Monaco
  ) {
    this.targetEditor = editorInstance;
    this.targetMonaco = monacoInstance;
  }

  // onChange

  onRuleEditorChange(value: string) {
    this.ruleEditorValue = value;
  }

  onTargetEditorChange(value: string) {
    this.targetEditorValue = value;
  }

  // actions

  registerKeyboardShortcuts() {
    if (this.ruleEditor && this.targetEditor && this.workbench.bundle) {
      this.ruleEditor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
        this.workbench.bundle.run
      );
      this.targetEditor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
        this.workbench.bundle.run
      );

      this.ruleEditor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
        this.workbench.saveOrFork
      );
      this.targetEditor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
        this.workbench.saveOrFork
      );
    }
  }

  renderTargetHighlights() {
    if (this.targetEditor === undefined) return;
    if (this.targetMonaco === undefined) return;
    if (this.workbench.bundle === null) return;
    if (this.workbench.bundle.language === null) return;

    const highlights =
      this.workbench.result?.highlights.filter((hl) => !hl.isError) || [];

    const highlightedWords = makeHighlightDec(highlights);
    const highlightedLines = makeHighlightLines(highlights, this.targetMonaco);

    this.latestHighlightIds = this.targetEditor.deltaDecorations(
      this.latestHighlightIds,
      [...highlightedLines, ...highlightedWords]
    );
  }

  focusTargetLine(line: number) {
    this.targetEditor?.revealLineInCenter(line);
  }

  // document markers

  get ruleMarkers(): monaco.editor.IMarkerData[] {
    let markers: monaco.editor.IMarkerData[] = [];

    for (const err of validateSemgrepYaml(this.ruleEditorValue ?? "")) {
      markers.push({
        source: err.source,
        code: err.code,
        message: err.message,
        severity: monaco.MarkerSeverity.Error,
        startLineNumber: err.startPos?.lineno ?? 1,
        startColumn: err.startPos?.col ?? 1,
        endLineNumber: err.endPos?.lineno ?? 1,
        endColumn: err.endPos?.col ?? 2,
      });
    }

    return markers;
  }

  updateRuleMarkers() {
    if (!this.ruleMonaco || !this.ruleEditor) return;
    const model = this.ruleEditor.getModel();
    if (!model) return;

    this.ruleMonaco.editor.setModelMarkers(model, "yaml", this.ruleMarkers);
  }
}
